import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpCircle, X } from "lucide-react";
import axios from "axios";
import { API } from "@/lib/config";

/**
 * Offers an update when the installed build is behind the one on Play.
 *
 * Android only. /api/app/version reports Android version codes and store_url is
 * the Play listing, so on iOS this compared an iOS build number against an
 * Android version code, always decided the app was behind, and sent the user to
 * Google Play in Safari. Apple rejects an app that points at another store, and
 * iOS installs updates from the App Store by itself, so there is nothing for this
 * to do there. The web app updates itself on reload, and a "get the app" pitch
 * belongs in the footer rather than in a modal.
 *
 * Play's own in-app-update flow is not reachable from a WebView, so the version
 * comparison is done against /api/app/version and the button opens the store
 * listing. That means the update is one tap away rather than automatic, which is
 * the honest ceiling for a Capacitor app without a native plugin.
 */
const SKIP_KEY = "tz.update.skipped";

export const UpdatePrompt = () => {
  const [info, setInfo] = useState(null);
  const [current, setCurrent] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (Capacitor.getPlatform() !== "android") return;
    let cancelled = false;

    const check = async () => {
      try {
        const { App } = await import("@capacitor/app");
        const appInfo = await App.getInfo();
        // Android reports build as a string; Play version codes are integers.
        const installed = parseInt(appInfo.build, 10);
        if (Number.isNaN(installed)) return;

        const { data } = await axios.get(`${API}/app/version`, { timeout: 8000 });
        if (cancelled || !data?.version_code) return;
        if (installed >= data.version_code) return;

        // Someone who said "Later" should not be asked again for the same build.
        let skipped = null;
        try {
          skipped = localStorage.getItem(SKIP_KEY);
        } catch {
          /* private mode - prompt again, which is the safer default */
        }
        if (String(skipped) === String(data.version_code) && !data.min_supported_version_code) return;

        setCurrent(installed);
        setInfo(data);
      } catch {
        /* offline, or the endpoint is unreachable - never block the app on this */
      }
    };

    // Not on the first paint: a cold start already has the feed to fetch, and a
    // modal arriving over an empty screen is worse than one arriving a moment later.
    const timer = setTimeout(check, 4000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, []);

  if (!info || dismissed) return null;

  const forced = current !== null && current < (info.min_supported_version_code || 0);

  const later = () => {
    try {
      localStorage.setItem(SKIP_KEY, String(info.version_code));
    } catch {
      /* nothing to remember it with - it will ask again next launch */
    }
    setDismissed(true);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] flex items-end justify-center bg-black/70 backdrop-blur-sm p-4 sm:items-center"
        onClick={forced ? undefined : later}
      >
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="tz-safe-bottom w-full max-w-sm rounded-2xl border border-white/10 bg-zinc-900 p-5 shadow-2xl"
        >
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-xl bg-orange-500/15 p-2">
              <ArrowUpCircle className="h-5 w-5 text-orange-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-heading text-base font-bold">Update available</p>
              <p className="mt-1 text-sm text-white/60">
                {forced
                  ? "This version is no longer supported. Update to carry on using TrendingZ."
                  : `TrendingZ ${info.version_name} is out. Update for the latest fixes.`}
              </p>
            </div>
            {!forced && (
              <button onClick={later} aria-label="Dismiss update prompt" className="rounded-md p-1 text-white/40 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="mt-4 flex gap-2">
            {!forced && (
              <button
                onClick={later}
                className="flex-1 rounded-xl border border-white/10 px-4 py-2.5 text-sm font-medium text-white/70 hover:bg-white/5"
              >
                Later
              </button>
            )}
            <a
              href={info.store_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 rounded-xl bg-gradient-to-r from-orange-500 to-yellow-500 px-4 py-2.5 text-center text-sm font-bold text-black"
            >
              Update
            </a>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
